import * as React from 'react';
import ErrorBoundary from './ErrorBoundary';
import LoadingScreen from './LoadingScreen';
import { usePageLoading } from '@/hooks/usePageLoading';
import { criticalLogger } from '@/utils/logger';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  routeName: string;
  preloadHints?: string[];
}

export const RouteErrorBoundary: React.FC<RouteErrorBoundaryProps> = ({
  children,
  routeName,
  preloadHints = []
}) => {
  const { setPageLoading, clearPageLoading } = usePageLoading();

  const handleError = (error: Error) => {
    criticalLogger.error(`Route error in ${routeName}:`, error);
    
    // Clear any loading states for this route
    clearPageLoading(`page-${routeName}-load` as any);
    clearPageLoading(`page-${routeName}-init` as any);
    
    // Track error for analytics
    // TODO: Send to analytics service
  };

  const handleRetry = () => {
    // Set loading state for retry
    setPageLoading(`page-${routeName}-retry` as any, true, 'Retrying...', 0);
    
    // Clear loading after a brief moment to allow re-render
    setTimeout(() => {
      clearPageLoading(`page-${routeName}-retry` as any);
    }, 500);
  };

  const fallbackComponent = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Failed to load {routeName}
        </h2>
        <p className="text-sm text-muted-foreground">
          There was an error loading this page. Please try again.
        </p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <ErrorBoundary
      context={`${routeName} route`}
      onError={handleError}
      fallback={fallbackComponent}
      showRefresh={true}
      showHome={true}
    >
      {children}
    </ErrorBoundary>
  );
};